// Admin API Service - Connected to Real Backend
import apiClient from '../../../api/apiClient';
import { API_BASE_URL } from '../../../configs/configs';

// Reviews API. Admins can edit (rating + comment) and delete; there is no moderation status.
export const reviewsAPI = {
  getAll: () => apiClient.get('/Review'),
  getByProduct: productId => apiClient.get(`/Review/product/${productId}`),
  // PUT /Review/{id} { rating, comment }
  update: (id, { rating, comment }) =>
    apiClient.put(`/Review/${id}`, { rating: Number(rating), comment }),
  delete: id => apiClient.delete(`/Review/${id}`),
};

// Videos: use src/services/videoService.js (/ProjectVideo).

// Orders API (admin)
export const ordersAPI = {
  // GET /Orders (admin). state: 'PaymentSubmitted' or 'Paid,Fulfilled'; dateEnd is inclusive.
  getAll: (filters = {}) =>
    apiClient.get('/Orders', {
      params: {
        OrderId: filters.orderId,
        State: filters.state || undefined,
        SearchTerm: filters.searchTerm || undefined,
        PaymentMethod: filters.paymentMethod,
        DateStart: filters.dateStart,
        DateEnd: filters.dateEnd,
        UserId: filters.userId,
        CurrentPage: filters.currentPage || 1,
        ItemsPerPage: filters.itemsPerPage || 10,
        SortBy: filters.sortBy,
        SortOrder: filters.sortOrder,
      },
    }),
  // GET /Orders/summary -> { total, byState: { PendingPayment, PaymentSubmitted, Paid, Fulfilled, PaymentFailed, Cancelled, Refunded } }
  getSummary: () => apiClient.get('/Orders/summary'),
  getById: id => apiClient.get(`/Orders/${id}`),
  getByOrderId: orderId => apiClient.get(`/Orders/order/${encodeURIComponent(orderId)}`),
  // PUT /Orders/update-status { orderId, status: PENDING | PAYMENT_COMPLETED | DELIVERED | PAYMENT_FAILED | CANCELLED | REFUNDED, description, deliver? }
  // deliver=false with PAYMENT_COMPLETED marks the order paid without delivering; it then waits for "Assign deliverables".
  updateStatus: (orderId, status, description, deliver) =>
    apiClient.put('/Orders/update-status', { orderId, status, description, deliver }),
  // GET /Github/verify-order-access/{orderId} -> data: { success, message?, accessDetails: [...] }
  verifyGithubAccess: orderId =>
    apiClient.get(`/Github/verify-order-access/${encodeURIComponent(orderId)}`),
  getMyOrders: (filters = {}) =>
    apiClient.get('/Orders/my-orders', {
      params: {
        Status: filters.status,
        DateStart: filters.dateStart,
        DateEnd: filters.dateEnd,
        CurrentPage: filters.currentPage || 1,
        ItemsPerPage: filters.itemsPerPage || 10,
        SortBy: filters.sortBy,
        SortOrder: filters.sortOrder,
      },
    }),
  getPaymentStatus: orderId =>
    apiClient.get(`/Orders/payment-status/${orderId}`),
  createDigitalOrder: data =>
    apiClient.post('/Orders/digital', {
      productId: data.productId,
      paymentMethod: data.paymentMethod || null,
      orderNotes: data.orderNotes || null,
    }),
};

// Users API. GET/PUT/DELETE are admin-only; DELETE is a soft delete (status -> Inactive).
export const usersAPI = {
  getAll: () => apiClient.get('/Users'),
  getById: id => apiClient.get(`/Users/${id}`),
  // POST /Users is the normal sign-up endpoint (RegisterRequest).
  create: data =>
    apiClient.post('/Users', {
      fullName: data.fullName,
      email: data.email || null,
      phoneNumber: data.phoneNumber,
      password: data.password,
      gitHubUsername: data.gitHubUsername,
    }),
  // PUT /Users/{id} { fullName?, phoneNumber?, gitHubUsername?, role?: 'User'|'Admin', status?: 'Active'|'Inactive'|'Suspended' }
  update: (id, data) => apiClient.put(`/Users/${id}`, data),
  deactivate: id => apiClient.delete(`/Users/${id}`),
};

/** The product body POST /Products and PUT /Products/{id} accept (and nothing else). */
const productBody = data => ({
  name: data.name,
  sortDescription: data.sortDescription,
  longDescription: data.longDescription ?? '',
  price: Number(data.price),
  category: data.category,
  keywords: data.keywords || [],
  tags: data.tags || [],
  whatInsideTheBox: data.whatInsideTheBox || [],
  features: data.features || [],
  technologies: data.technologies || [],
  isActive: data.isActive ?? true,
  isTrending: data.isTrending ?? false,
  isFeatured: data.isFeatured ?? false,
  visibility: data.visibility || 'Public',
  productType: 'Digital',
  media: (data.media || []).map(m => ({
    title: m.title || '',
    url: m.url,
    type: 'image',
    mimeType: m.mimeType || 'image/jpeg',
    fileSizeInBytes: Number(m.fileSizeInBytes) || 0,
  })),
  digitalProductDetails: {
    edition: data.digitalProductDetails?.edition || '',
    contentDescription: data.digitalProductDetails?.contentDescription || '',
    estimatedDownloadSizeMB: Math.max(0, Math.round(Number(data.digitalProductDetails?.estimatedDownloadSizeMB) || 0)),
  },
  additionalDetails: data.additionalDetails || {},
});

const ADMIN_PAGE_SIZE = 100;
const MAX_ADMIN_PAGES = 20;

// Products API. With an admin token, IncludeHidden=true also returns inactive and non-public products.
export const productsAPI = {
  getAll: (filters = {}) =>
    apiClient.get('/Products', {
      params: {
        IncludeHidden: filters.includeHidden,
        SearchTerm: filters.searchTerm,
        Category: filters.category,
        CurrentPage: filters.currentPage || 1,
        ItemsPerPage: filters.itemsPerPage || 10,
        SortBy: filters.sortBy,
        SortOrder: filters.sortOrder,
      },
    }),
  /** Every product, hidden ones included, fetched 100 at a time. Resolves with an array. */
  getAllForAdmin: async () => {
    const all = [];
    for (let page = 1; page <= MAX_ADMIN_PAGES; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const response = await productsAPI.getAll({
        includeHidden: true,
        itemsPerPage: ADMIN_PAGE_SIZE,
        currentPage: page,
      });
      const data = response.data?.data || {};
      const products = Array.isArray(data.products) ? data.products : [];
      all.push(...products);
      const totalPages = Number(data.totalPages) || 1;
      if (page >= totalPages || products.length < ADMIN_PAGE_SIZE) break;
    }
    return all;
  },
  getById: id => apiClient.get(`/Products/${id}`),
  create: data => apiClient.post('/Products', productBody(data)),
  // PUT returns 404 when the product no longer exists.
  update: (id, data) => apiClient.put(`/Products/${id}`, productBody(data)),
  delete: id => apiClient.delete(`/Products/${id}`),
  /** Active categories in display order: GET /Category. Resolves with an array. */
  getCategories: async () => {
    const response = await apiClient.get('/Category', { params: { IsActive: true, PageSize: 100 } });
    const list = Array.isArray(response.data?.data) ? response.data.data : [];
    return list
      .filter(c => c && c.isActive !== false && c.name)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name));
  },
};

/** Query parameters shared by the payment list and its summary (dates are ISO strings; endDate is inclusive). */
const paymentParams = (filters = {}) => ({
  State: filters.state || undefined,
  SearchTerm: filters.searchTerm || undefined,
  PaymentMethod: filters.paymentMethod || undefined,
  StartDate: filters.startDate || undefined,
  EndDate: filters.endDate || undefined,
});

// Transactions = payment attempts (admin)
export const transactionsAPI = {
  // GET /api/Payment/transactions -> { items, totalItems, currentPage, pageSize, totalPages }
  getAll: (filters = {}) =>
    apiClient.get('/api/Payment/transactions', {
      params: { ...paymentParams(filters), Page: filters.page || 1, PageSize: filters.pageSize || 20 },
    }),
  // GET /api/Payment/summary -> { total, byState: { Created, Pending, Submitted, Succeeded, Failed, Refunded }, succeededAmount }
  getSummary: (filters = {}) => apiClient.get('/api/Payment/summary', { params: paymentParams(filters) }),
};

// Categories API (using real Category endpoints)
export const categoriesAPI = {
  getAll: (filters = {}) =>
    apiClient.get('/Category', {
      params: {
        Name: filters.name,
        IsActive: filters.isActive,
        ParentCategoryId: filters.parentCategoryId,
        PageNumber: filters.pageNumber || 1,
        PageSize: filters.pageSize || 10,
      },
    }),
  getById: id => apiClient.get(`/Category/${id}`),
  getByName: name => apiClient.get(`/Category/by-name/${name}`),
  create: data =>
    apiClient.post('/Category', {
      name: data.name,
      description: data.description || null,
      imageUrl: data.imageUrl || null,
      parentCategoryId: data.parentCategoryId || null,
      isActive: data.isActive ?? true,
      displayOrder: data.displayOrder || 0,
    }),
  update: (id, data) =>
    apiClient.put(`/Category/${id}`, {
      name: data.name || null,
      description: data.description || null,
      imageUrl: data.imageUrl || null,
      parentCategoryId: data.parentCategoryId || null,
      isActive: data.isActive ?? null,
      displayOrder: data.displayOrder ?? null,
    }),
  delete: id => apiClient.delete(`/Category/${id}`),
};

// Settings (Admin → Settings). Saved values apply right away; secrets are write-only (only isSet/hint come back).
export const settingsAPI = {
  // GET /api/admin/settings -> { sections: [{ id, group, title, description, fields, lastChangedAt, lastChangedBy }], status, history }
  getAll: () => apiClient.get('/api/admin/settings'),
  // PUT /api/admin/settings/{sectionId} { values: { key: value }, reset: [key] } -> { section, status, change }
  // A 400 carries field errors as data: { key: message }.
  save: (sectionId, values, reset) =>
    apiClient.put(`/api/admin/settings/${encodeURIComponent(sectionId)}`, { values, reset }),
  // POST /api/admin/settings/email/test { to? } -> sends straight through SMTP, so it can take a while.
  sendTestEmail: to =>
    apiClient.post('/api/admin/settings/email/test', { to: to || null }, { timeout: 75000 }),
};

/** Fired on window after an enquiry changes, so the nav's "new" count refreshes. */
export const ENQUIRIES_CHANGED_EVENT = 'pk:admin-enquiries-changed';

// Enquiries inbox (contact form and custom-project requests).
export const enquiriesAPI = {
  // GET /api/enquiries?Status=&Type=&SearchTerm=&CurrentPage=&ItemsPerPage= -> { items, totalCount, currentPage, itemsPerPage, totalPages }
  getAll: (filters = {}) =>
    apiClient.get('/api/enquiries', {
      params: {
        Status: filters.status || undefined,
        Type: filters.type || undefined,
        SearchTerm: filters.searchTerm || undefined,
        CurrentPage: filters.currentPage || 1,
        ItemsPerPage: filters.itemsPerPage || 20,
      },
    }),
  // PATCH /api/enquiries/{id}/status { status: 'new'|'in_progress'|'closed', note? }
  updateStatus: (id, status, note) =>
    apiClient.patch(`/api/enquiries/${id}/status`, { status, note: note ?? null }),
  delete: id => apiClient.delete(`/api/enquiries/${id}`),
};

// Dashboard: one server-side summary (counts by state, paid revenue, latest items). See src/services/dashboardService.js.
export const dashboardAPI = {
  getStats: () => apiClient.get('/api/Dashboard'),
};

// GitHub codes: use src/services/githubService.js.

// Deliverables API. The admin screens use src/services/deliverableService.js.
export const deliverablesAPI = {
  // GET /api/Deliverable (admin) -> every product's deliverable
  getAll: () => apiClient.get('/api/Deliverable'),
  // POST /api/Deliverable creates or replaces the product's deliverable (one per product)
  create: data =>
    apiClient.post('/api/Deliverable', {
      productId: data.productId,
      edition: data.edition || 'Standard',
      deliverableContent: data.deliverableContent || [],
      accessInstructions: data.accessInstructions || '',
      licenseTerms: data.licenseTerms || '',
      isActive: data.isActive ?? true,
    }),
  getByOrder: orderId => apiClient.get(`/api/Deliverable/order/${orderId}`),
  deleteByProduct: productId =>
    apiClient.delete(`/api/Deliverable/product/${productId}`),
  trackAccess: data =>
    apiClient.post('/api/Deliverable/track-access', {
      orderId: data.orderId || null,
      productId: data.productId || null,
    }),
};

// Payment API (admin)
export const paymentAPI = {
  // Approve (marks the order paid and, unless deliver=false, delivers it) or reject a manual UPI payment.
  // paymentId is payment.id, not transactionId.
  reviewPayment: (paymentId, approve, note, deliver) =>
    apiClient.post(`/api/payments/${paymentId}/review`, { approve, note, deliver }),
  // Latest payment attempt of an order (404 when the order has none)
  getByOrder: orderId => apiClient.get(`/api/Payment/order/${encodeURIComponent(orderId)}`),
};

// Health Check API
export const healthAPI = {
  check: () => apiClient.get('/Health'),
  checkDatabase: () => apiClient.get('/Health/database'),
};

export default apiClient;
